import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { AtomicItemListType, type IScreensComponent } from 'shared/src/api';
import { isNil } from 'shared/src/utils';
import type { AtomicRendererRefCurrent } from '../types';

type UseScrollToFilterListProps = {
  components: Array<IScreensComponent> | undefined;
  atomicRendererRef: RefObject<AtomicRendererRefCurrent>;
  isFetching: boolean;
  itemHeightsRef: RefObject<{ [key: number]: number }>;
};

export const useScrollToFilterList = ({
  atomicRendererRef,
  components,
  isFetching,
  itemHeightsRef,
}: UseScrollToFilterListProps) => {
  const filterListIndex = useMemo(() => {
    const index = components?.findIndex(
      component => component.type.itemList === AtomicItemListType.FilterList,
    );
    return isNil(index) || index === -1 ? undefined : index;
  }, [components]);

  const [isPendingScroll, setIsPendingScroll] = useState(false);
  const pendingScrollRef = useRef(false);
  const scrolledOffsetRef = useRef(0);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const getFilterListOffset = useCallback(() => {
    if (isNil(filterListIndex)) {
      return 0;
    }
    // scrollToIndex는 가변 높이(PROXY 등)에서 offset 추정이 부정확하므로
    // 측정된 아이템 높이를 합산하여 scrollToOffset 사용
    return Array.from({ length: filterListIndex + 1 }, (_, i) =>
      itemHeightsRef.current[i] ?? 0,
    ).reduce((totalOffset, height) => totalOffset + height, 0);
  }, [filterListIndex, itemHeightsRef]);

  // 필터 변경 후 데이터가 안정되면 스크롤 실행
  // isFetching 중이면 대기, components 변경 시 타이머 리셋 (loadmore 연쇄 debounce)
  useEffect(() => {
    if (!pendingScrollRef.current || isFetching) {
      return;
    }

    scrollTimerRef.current = setTimeout(() => {
      if (!pendingScrollRef.current) {
        return;
      }
      pendingScrollRef.current = false;
      setIsPendingScroll(false);

      const offset = getFilterListOffset();
      if (offset > 0) {
        atomicRendererRef.current?.scrollToOffset({ offset, animated: true });
        scrolledOffsetRef.current = offset;
      }
    }, 300);

    return () => clearTimeout(scrollTimerRef.current);
  }, [components, isFetching, getFilterListOffset, atomicRendererRef]);

  // 2차 보정: FILTER_LIST 이전 아이템 높이 변경 시 (PROXY 재측정 등)
  // 1차 스크롤 후 PROXY 등이 화면에 들어와 재렌더링되면 onLayout에서 높이 변경 감지 → 보정
  const onItemHeightChange = useCallback(
    (index: number) => {
      if (isNil(filterListIndex) || index > filterListIndex) {
        return;
      }

      const correctedOffset = getFilterListOffset();
      if (
        correctedOffset > 0 &&
        scrolledOffsetRef.current > 0 &&
        correctedOffset !== scrolledOffsetRef.current
      ) {
        scrolledOffsetRef.current = correctedOffset;
        atomicRendererRef.current?.scrollToOffset({
          offset: correctedOffset,
          animated: true,
        });
      }
    },
    [filterListIndex, getFilterListOffset, atomicRendererRef],
  );

  const scrollToFilterList = useCallback(() => {
    if (isNil(filterListIndex)) {
      return;
    }
    pendingScrollRef.current = true;
    setIsPendingScroll(true);
    scrolledOffsetRef.current = 0;
  }, [filterListIndex]);

  return {
    scrollToFilterList,
    filterListIndex,
    onItemHeightChange,
    isPendingScroll,
  };
};
